/**
 * The shapes the search index returns.
 *
 * Mirrors functions/src/algolia/types.ts. Kept as its own file rather than
 * imported across the package boundary because the browser needs only the
 * handful of fields the result list renders, and the backend record carries
 * whole documents' worth of extracted text that must never be typed as
 * something a component might casually render.
 */

/** Statuses the index can return. Wire values, snake_case. */
export type SearchStatus =
  | 'draft'
  | 'in_review'
  | 'pending_review'
  | 'pending_approval'
  | 'approved'
  | 'published'
  | 'archived'
  | 'superseded';

export interface HighlightValue {
  value: string;
  matchLevel: 'none' | 'partial' | 'full';
}

export interface SearchHit {
  objectID: string;
  policyId: string;
  versionId: string;
  policyNumber: string;
  title: string;
  summary?: string;
  status: SearchStatus;
  departmentId?: string;
  departmentName?: string;
  categoryId?: string;
  categoryName?: string;
  documentTypeId?: string;
  documentTypeName?: string;
  ownerUid?: string;
  ownerName?: string;
  tagNames?: string[];
  regulatoryStandardCodes?: string[];
  updatedAt?: number;
  publishedAt?: number;

  _highlightResult?: {
    title?: HighlightValue;
    policyNumber?: HighlightValue;
    summary?: HighlightValue;
    tagNames?: HighlightValue[];
    attachmentFileNames?: HighlightValue[];
  };
  _snippetResult?: {
    summary?: HighlightValue;
    editorMarkdownText?: HighlightValue;
    attachmentText?: HighlightValue;
  };
}

/** Which facets the UI offers, in the order they are shown. */
export const FACET_FIELDS = [
  'departmentId',
  'documentTypeId',
  'categoryId',
  'ownerUid',
  'status',
  'regulatoryStandardIds',
] as const;

export type FacetField = (typeof FACET_FIELDS)[number];

export const FACET_LABELS: Record<FacetField, string> = {
  departmentId: 'Department',
  documentTypeId: 'Document type',
  categoryId: 'Category',
  ownerUid: 'Owner',
  status: 'Status',
  regulatoryStandardIds: 'Standard',
};

export type FacetCounts = Partial<Record<FacetField, Record<string, number>>>;

export type FacetSelection = Partial<Record<FacetField, string>>;

export interface SearchResult {
  hits: SearchHit[];
  /** Total matches in the index, which may exceed what was returned. */
  totalHits: number;
  /** True when totalHits is an estimate rather than exhaustive. */
  exhaustive: boolean;
  facets: FacetCounts;
  processingTimeMs: number;
  query: string;
  /**
   * True when more documents matched than the ceiling will hand back.
   *
   * The results are still shown — the best `maxResults` of them, ranked. This
   * only says the tail was cut, so the page can suggest narrowing rather than
   * implying the list is everything.
   */
  tooMany: boolean;

  /** How many of the matches are actually reachable by paging. */
  reachableHits: number;

  /** Zero-based index of the last page that can be opened. */
  lastPage: number;

  /** Zero-based index of the page these hits came from. */
  page: number;
}

/** What the backend hands back when it issues a search key. */
export interface SearchKey {
  searchKey: string;
  appId: string;
  indexName: string;
  /** Unix seconds. */
  validUntil: number;
  canSearchUnpublished: boolean;
  boostDepartmentIds: string[];
  maxResults: number;
}
